import { BaseUniDriver, UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { baseUniDriverFactory } from '../../../testkitUtils/unidriver';
import {
  dropdownLayoutDriverFactory,
  DropdownLayoutUniDriver,
} from 'wix-style-react-tpa/dist/src/DropdownLayout/DropdownLayout.uni.driver';
import { iconButtonDriverFactory } from 'wix-style-react-tpa/dist/src/IconButton/IconButton.uni.driver';
import { dataHooks } from './constants';

export interface CalendarUniDriver extends BaseUniDriver {
  exists: () => Promise<boolean>;
  close: () => Promise<any>;
  getCurrentMonthWithYear: () => Promise<string>;
  getMonthAndYear: () => Promise<string[]>;
  getNthWeekDayName: (n: number) => Promise<string>;
  clickOnNthDay: (n: number) => Promise<any>;
  clickDay: (date: Date) => Promise<any>;
  isDayActive: (date: Date) => Promise<boolean>;
  clickOnNthDayOfTheMonth: (n: number) => Promise<any>;
  clickOnSelectedDay: () => Promise<any>;
  clickOnYearDropdown: () => Promise<any>;
  clickOnMonthDropdown: () => Promise<any>;
  clickOnNthYear: (n: number) => Promise<any>;
  clickOnPrevMonthButton: () => Promise<any>;
  clickOnNextMonthButton: () => Promise<any>;
  keyboardNextYear: () => Promise<void>;
  keyboardPrevYear: () => Promise<void>;
  isHeaderExists: () => Promise<boolean>;
  isYearDropdownExists: () => Promise<boolean>;
  isYearCaptionExists: () => Promise<boolean>;
  isMonthDropdownExists: () => Promise<boolean>;
  isMonthCaptionExists: () => Promise<boolean>;
  getMonthCaption: () => Promise<string>;
  getMonthDropdownLabel: () => Promise<string>;
  getSelectedYear: () => Promise<string>;
  getFocusedDay: () => Promise<string | null>;
  getFocusedDayElement: () => UniDriver;
  pressLeftArrow: () => Promise<any>;
  pressRightArrow: () => Promise<any>;
  getSelectedDay: () => Promise<string>;
  triggerKeyDown: ({ key }?: { key: string }) => Promise<any>;
  isFocusedDayVisuallyUnfocused: () => Promise<boolean>;
  isTwoMonthsLayout: () => Promise<boolean>;
  getMonthDropdownDriver: () => DropdownLayoutUniDriver;
  getYearDropdownDriver: () => DropdownLayoutUniDriver;
  getNumOfVisibleMonths: () => Promise<number>;
  getNumOfSelectedDays: () => Promise<number>;
  getSelectedDays: () => Promise<number>;
  getSize: () => Promise<string>;
}

export const calendarUniDriverFactory = (base): CalendarUniDriver => {
  const getDays = () =>
    base.$$(
      '[role="gridcell"]:not([class*="disabled"])>[data-outside="false"]'
    );

  const getNthDay = (n) => getDays().get(n);
  const getNthDayOfTheMonth = (n) =>
    base.$$(`[role="gridcell"]>[data-outside="false"]`).get(n);

  const getDayOfDate = async (date) => {
    const selector = `[role="gridcell"]${getDateDayAttr(date)} button`;
    return base.$(selector);
  };

  const getDateDayAttr = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `[data-day="${year}-${month}-${day}"]`;
  };

  const getSelectedDay = () =>
    base.$('[role="gridcell"][data-selected="true"] button');

  const getYearDropdownButton = () =>
    base.$('[data-hook="datepicker-year-dropdown-button"]');
  const getMonthDropdownButton = () =>
    base.$('[data-hook="datepicker-month-dropdown-button"]');
  const getNthYear = (n) => base.$(`[data-hook="dropdown-item-${n}"]`);
  const getMonthCaption = () =>
    base.$$('[data-hook="datepicker-month-caption"]').get(0);
  const getYearCaption = () => base.$('[data-hook="datepicker-year-caption"]');
  const getMonthAndYear = () => [getMonthCaption(), getYearCaption()];
  const getNthWeekDayName = (n) => base.$$(`th`).get(n);
  const getPrevMonthButton = () =>
    base.$('[data-hook="datepicker-left-arrow"]');
  const getNextMonthButton = () =>
    base.$('[data-hook="datepicker-right-arrow"]');
  const getFocusedDay = () => base.$('[data-focused="true"]');
  const getVisuallyUnfocusedDay = () => base.$('.unfocused');
  const getMonthContainers = () => base.$$('.DayPicker-Month');
  const getVisibleMonths = () => base.$$('[class*="DayPicker-Month"]');
  const getSelectedDays = () =>
    base.$$('[role="gridcell"][aria-selected="true"]>[data-outside="false"]');
  const getMonthDropdown = () =>
    base.$('[data-hook="datepicker-month-dropdown"]');
  const getYearDropdown = () =>
    base.$('[data-hook="datepicker-year-dropdown"]');
  const getHeader = () => base.$('[data-hook="datepicker-head"]');

  const isDayDisabled = async (date): Promise<boolean> =>
    (
      await base.$(
        `[role="gridcell"][data-disabled="true"]${getDateDayAttr(date)}`
      )
    ).exists();

  return {
    ...baseUniDriverFactory(base),
    close: () => getFocusedDay().pressKey('Escape'),
    getCurrentMonthWithYear: () =>
      Promise.all(getMonthAndYear().map((elm) => elm.text())).then((values) =>
        values.join(' ')
      ),
    getNthWeekDayName: async (n = 0) =>
      (await getNthWeekDayName(n).exists()) ? getNthWeekDayName(n).text() : '',
    clickOnNthDay: async (n = 0) =>
      (await getNthDay(n).exists()) && getNthDay(n).click(),
    clickDay: async (date) => {
      const day = await getDayOfDate(date);
      if (await day.exists()) {
        await day.click();
      } else {
        throw new Error(
          `ERROR: CalendarDriver.clickDay() - The given date (${date.toString()}) is not visible`
        );
      }
    },

    isDayActive: async (date) => {
      const value = await getDayOfDate(date);
      if (await value.exists()) {
        return !(await isDayDisabled(date));
      } else {
        throw new Error(
          `ERROR: CalendarDriver.isDayActive() - The given date (${date.toString()}) is not visible`
        );
      }
    },
    clickOnNthDayOfTheMonth: async (n = 0) =>
      (await getNthDayOfTheMonth(n).exists()) && getNthDayOfTheMonth(n).click(),
    clickOnSelectedDay: () => getSelectedDay().click(),
    clickOnYearDropdown: () => getYearDropdownButton().click(),
    clickOnMonthDropdown: () => getMonthDropdownButton().click(),
    clickOnNthYear: (n = 1) => getNthYear(n).mouse.press(),
    clickOnPrevMonthButton: () =>
      iconButtonDriverFactory(getPrevMonthButton()).click(),
    clickOnNextMonthButton: () =>
      iconButtonDriverFactory(getNextMonthButton()).click(),
    keyboardNextYear: () => getSelectedDay().pressKey('ArrowDown'),
    keyboardPrevYear: () => getSelectedDay().pressKey('ArrowUp'),
    isHeaderExists: () => getHeader().exists(),
    isYearDropdownExists: () => getYearDropdown().exists(),
    isYearCaptionExists: () => getYearCaption().exists(),
    isMonthDropdownExists: () => getMonthDropdown().exists(),
    isMonthCaptionExists: () => getMonthCaption().exists(),
    getMonthCaption: () => getMonthCaption().text(),
    getMonthDropdownLabel: () => getMonthDropdownButton().text(),
    getSelectedYear: () => getYearDropdownButton().text(),
    /** Returns the text of the focused day or `null` if there is no focused day */
    getFocusedDay: async () => {
      const focusedDayElement = getFocusedDay();
      return (await focusedDayElement.exists())
        ? focusedDayElement.text()
        : null;
    },
    getFocusedDayElement: () => getFocusedDay(),
    pressLeftArrow: () => getFocusedDay().pressKey('ArrowLeft'),
    pressRightArrow: () => getFocusedDay().pressKey('ArrowRight'),
    getSelectedDay: () => getSelectedDay().text(),
    triggerKeyDown: ({ key }) => getFocusedDay().pressKey(key),
    isFocusedDayVisuallyUnfocused: () => getFocusedDay().hasClass('unfocused'),
    containsVisuallyUnfocusedDay: () => getVisuallyUnfocusedDay().exists(),
    isTwoMonthsLayout: async () => (await getMonthContainers().count()) === 2,

    // @ts-expect-error TODO: JB:
    getMonthDropdownDriver: async () => {
      await getMonthDropdownButton().click();
      return dropdownLayoutDriverFactory(await getMonthDropdown());
    },

    // @ts-expect-error TODO: JB:
    getYearDropdownDriver: async () => {
      await getYearDropdownButton().click();
      return dropdownLayoutDriverFactory(await getYearDropdown());
    },
    getNumOfVisibleMonths: () => getVisibleMonths().count(),
    getNumOfSelectedDays: () => getSelectedDays().count(),
    // @ts-expect-error TODO: JB:
    getSelectedDays: () => {
      const datesPromises = getSelectedDays().map(async (item) => {
        const attr = await item.attr('data-day');
        const date = attr.split('-').map((part) => parseInt(part, 10));

        return new Date(date[0], date[1], date[2]);
      });
      return Promise.all(datesPromises);
    },
    /** Returns size property value currently set on component
     * @returns {Promise<string>}
     */
    getSize: () =>
      base.$(`[data-hook="${dataHooks.baseCalendar}"]`).attr('data-size'),

    getWeeks: () => base.$$('tbody.rdp-weeks tr'),
  };
};
